import axios from "axios";
import { LinkedEstablishment } from "./restaurantNews";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/**
 * Admin enrichment boost (`/admin/enrichment-boost`, admin only): enrich one establishment now,
 * on the fields chosen, every pipeline gate skipped — calls still count in the quotas. Event-level
 * results are reviewed with the usual `/admin/events/enrichment-suggestions` endpoints.
 */

export type BoostField = "owners" | "google" | "website" | "email" | "linkedin";
export type BoostStepStatus = "running" | "done" | "empty" | "error" | "blocked";

export interface BoostStep {
    status: BoostStepStatus;
    message?: string;
    startedAt: string;
    finishedAt?: string;
}

/** One enrichment entry as the pipeline stores it (event or person). */
export interface BoostEntry {
    type: string;
    status: "pending" | "processing" | "waiting_callback" | "done" | "approved" | "rejected" | "retry" | "error" | "closed" | "unsure";
    data: any;
    options: any[];
    error: string | null;
    lastRunAt: string | null;
    reviewedAt: string | null;
    isManuallyReviewed: boolean;
    searchKeyword: string | null;
}

export interface BoostPerson {
    _id: string;
    name: string;
    qualite: string | null;
    role: string | null;
    linkedin: BoostEntry | null;
}

export interface BoostState {
    establishmentId: string;
    siren: string | null;
    siret: string | null;
    website: string | null;
    eventId: string | null;
    run: { startedAt: string | null; steps: Partial<Record<BoostField, BoostStep>> } | null;
    fields: { google: BoostEntry | null; website: BoostEntry | null; email: BoostEntry | null };
    people: BoostPerson[];
    /** The establishment in the news feed's shape, to repaint the panel. */
    establishment: LinkedEstablishment | null;
}

/** The event enrichment type behind each event-level field. */
export const BOOST_EVENT_TYPES = { google: "google_place_DFS", website: "website", email: "website_content_OTS" } as const;

const base = (establishmentId: string) => `${API_URL}/admin/enrichment-boost/${establishmentId}`;

export const getBoostState = async (establishmentId: string): Promise<BoostState> => {
    const res = await axios.get(base(establishmentId), { headers: authHeaders() });
    return res.data;
};

export const startBoost = async (
    establishmentId: string,
    fields: BoostField[]
): Promise<{ started: BoostField[]; skipped: { field: BoostField; reason: string }[]; state: BoostState }> => {
    const res = await axios.post(base(establishmentId), { fields }, { headers: authHeaders() });
    return res.data;
};

export const approveEventEnrichment = async (eventId: string, enrichmentType: string, selectedOption: any): Promise<void> => {
    await axios.post(
        `${API_URL}/admin/events/enrichment-suggestions/${eventId}/approve`,
        { enrichmentType, selectedOption },
        { headers: authHeaders() }
    );
};

export const rejectEventEnrichment = async (eventId: string, enrichmentType: string): Promise<void> => {
    await axios.post(`${API_URL}/admin/events/enrichment-suggestions/${eventId}/reject`, { enrichmentType }, { headers: authHeaders() });
};

export const approveOwnerLinkedin = async (establishmentId: string, personId: string, option: any): Promise<BoostState> => {
    const res = await axios.post(`${base(establishmentId)}/people/${personId}/linkedin/approve`, { option }, { headers: authHeaders() });
    return res.data;
};

export const rejectOwnerLinkedin = async (establishmentId: string, personId: string): Promise<BoostState> => {
    const res = await axios.post(`${base(establishmentId)}/people/${personId}/linkedin/reject`, {}, { headers: authHeaders() });
    return res.data;
};
